import React, { useState, useEffect } from "react";
import { C, getPerms } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  StatusBadge, DataTable, MeetingsTab, ReportsTab, useToast,
  OperationalPlanTab, TransactionsTab
} from "./erp-shared";
import { MonthlyReportButton } from "./erp-monthly-report";
import { DecisionsTab } from "./BoardPage";
import {
  getGovPolicies, createGovPolicy, getGovRisks, createGovRisk,
  getGovAudits, createGovAudit, getGovConflicts, createGovConflict,
  getGovLicenses, createGovLicense, getGovFinStatements, createGovFinStatement,
  getGovProjects, createGovProject,
  getGovGoals, createGovGoal, getGovSurveys, createGovSurvey,
  getGovCompliance, createGovCompliance
} from "./supabaseClient";
import {
  Shield, FileText, Plus, Search, CheckCircle, Clock,
  AlertCircle, Calendar, Target,
  AlertTriangle, ClipboardCheck,
  Layers, Award, TrendingUp,
  XCircle, ShieldCheck, Scroll, ListChecks,
  DollarSign, BarChart3, FileWarning, Smile,
  Receipt, Building, Edit, Gavel, Rocket, LayoutDashboard
} from "lucide-react";

/* ✦ محوّلات صفوف DB (snake_case) → camelCase الذي تقرأه الواجهة. */
function mapPolicyRow(r){return{id:r.id,number:r.number||"",title:r.title,category:r.category||"",status:r.status||"draft",issueDate:r.issue_date||"",lastUpdate:r.last_update||"",owner:r.owner||"—",summary:r.summary||"",color:r.color||"#6366f1",keyPoints:r.key_points||[]};}
function mapRiskRow(r){return{id:r.id,code:r.code||"",title:r.title,category:r.category||"",level:r.level||"medium",impact:r.impact||"",probability:r.probability||"",mitigation:r.mitigation||"—"};}
function mapAuditRow(r){return{id:r.id,number:r.number||"",title:r.title,scope:r.scope||"",auditor:r.auditor||"—",date:r.audit_date||"",findings:Number(r.findings)||0,openFindings:Number(r.open_findings)||0,status:r.status||"planned"};}
function mapConflictRow(r){return{id:r.id,member:r.member,nature:r.nature||"",action:r.action||"",date:r.conflict_date||""};}
function mapLicenseRow(r){return{id:r.id,number:r.number||"",purpose:r.purpose||"",authority:r.authority||"",issueDate:r.issue_date||"",expiryDate:r.expiry_date||"",status:r.status||"active"};}
function mapFinRow(r){return{id:r.id,name:r.name,type:r.type||"",period:r.period||"",status:r.status||"مسودة",total:Number(r.total)||0,notes:r.notes||""};}
function mapProjectRow(r){return{id:r.id,name:r.name,description:r.description||"",owner:r.owner||"—",startDate:r.start_date||"",endDate:r.end_date||"",progress:Number(r.progress)||0,status:r.status||"in_progress"};}
function mapComplianceRow(r){return{id:r.id,title:r.title,authority:r.authority||"—",category:r.category||"تنظيمي",deadline:r.deadline||"",status:r.status||"pending",owner:r.owner||"—",notes:r.notes||""};}

export default function GovernancePage({ user }) {
  const [tab, setTab] = useState("overview");
  const perms = getPerms(user);

  const [policies, setPolicies] = useState([]);
  useEffect(() => { getGovPolicies().then(d => setPolicies((d || []).map(mapPolicyRow))).catch(() => {}); }, []);

  const [risks, setRisks] = useState([]);
  useEffect(() => { getGovRisks().then(d => setRisks((d || []).map(mapRiskRow))).catch(() => {}); }, []);

  const [audits, setAudits] = useState([]);
  useEffect(() => { getGovAudits().then(d => setAudits((d || []).map(mapAuditRow))).catch(() => {}); }, []);

  const [conflicts, setConflicts] = useState([]);
  useEffect(() => { getGovConflicts().then(d => setConflicts((d || []).map(mapConflictRow))).catch(() => {}); }, []);

  const [licenses, setLicenses] = useState([]);
  useEffect(() => { getGovLicenses().then(d => setLicenses((d || []).map(mapLicenseRow))).catch(() => {}); }, []);

  const [finStatements, setFinStatements] = useState([]);
  useEffect(() => { getGovFinStatements().then(d => setFinStatements((d || []).map(mapFinRow))).catch(() => {}); }, []);

  const [govProjects, setGovProjects] = useState([]);
  useEffect(() => { getGovProjects().then(d => setGovProjects((d || []).map(mapProjectRow))).catch(() => {}); }, []);

  const [compliance, setCompliance] = useState([]);
  useEffect(() => { getGovCompliance().then(d => setCompliance((d || []).map(mapComplianceRow))).catch(() => {}); }, []);

  const tabs = [
    { key: "overview",     label: "نظرة عامة",          icon: LayoutDashboard },
    { key: "strategic",    label: "الخطة الاستراتيجية", icon: Target },
    { key: "plan",         label: "الخطة التشغيلية",   icon: BarChart3 },
    { key: "policies",     label: "اللوائح والسياسات",  icon: Scroll },
    { key: "financials",   label: "القوائم المالية",   icon: DollarSign },
    { key: "compliance",   label: "درجة الحوكمة",      icon: ClipboardCheck },
    { key: "conflict",     label: "تضارب المصالح",     icon: FileWarning },
    { key: "licenses",     label: "تراخيص جمع التبرعات", icon: Receipt },
    { key: "satisfaction", label: "نتائج قياس الرضا",   icon: Smile },
    { key: "risks",        label: "المخاطر",            icon: AlertTriangle },
    { key: "audit",        label: "سجل التدقيق",        icon: ShieldCheck },
    { key: "meetings",     label: "الاجتماعات",         icon: Calendar },
    { key: "decisions",    label: "القرارات",           icon: Gavel },
    { key: "projects",     label: "المشروعات",          icon: Rocket },
    { key: "transactions", label: "المعاملات",          icon: Receipt },
    { key: "reports",      label: "التقارير",           icon: FileText },
  ];

  const reportSources = [
    {
      label: "سجل التدقيق", mode: "monthly", dateField: "date", rows: audits,
      columns: [
        { key: "number", label: "الرقم" },
        { key: "title", label: "العنوان" },
        { key: "scope", label: "النطاق" },
        { key: "auditor", label: "المدقق" },
        { key: "date", label: "التاريخ" },
        { key: "findings", label: "الملاحظات" },
      ],
    },
    {
      label: "تضارب المصالح", mode: "monthly", dateField: "date", rows: conflicts,
      columns: [
        { key: "member", label: "العضو" },
        { key: "nature", label: "طبيعة التعارض" },
        { key: "action", label: "الإجراء المتخذ" },
        { key: "date", label: "التاريخ" },
      ],
    },
    {
      label: "نتائج قياس الرضا", mode: "monthly", dateField: "date", rows: SATISFACTION_SURVEYS,
      columns: [
        { key: "title", label: "الاستبيان" },
        { key: "audience", label: "الفئة" },
        { key: "score", label: "النسبة" },
        { key: "responses", label: "عدد الردود" },
        { key: "date", label: "التاريخ" },
      ],
    },
    { label: "اللوائح والسياسات", mode: "cumulative", rows: policies },
    { label: "المخاطر", mode: "cumulative", rows: risks },
    { label: "تراخيص جمع التبرعات", mode: "cumulative", rows: licenses },
    { label: "الالتزامات النظامية", mode: "cumulative", rows: compliance },
  ];

  return (
    <div>
      <ModuleHeader icon={Shield} title="الحوكمة" subtitle="الحوكمة المؤسسية والامتثال وإدارة المخاطر والتدقيق والشفافية">
        <MonthlyReportButton moduleTitle="الحوكمة" sources={reportSources} budget={300000} />
      </ModuleHeader>
      <TabBar tabs={tabs} active={tab} onChange={setTab} />
      {tab === "overview"     && <GovOverviewTab />}
      {tab === "strategic"    && <StrategicPlanTab />}
      {tab === "plan"         && <OperationalPlanTab departmentKey="governance" />}
      {tab === "policies"     && <PoliciesTab canEdit={perms.canEdit} data={policies} setData={setPolicies} />}
      {tab === "financials"   && <FinancialsTab statements={finStatements} setStatements={setFinStatements} />}
      {tab === "compliance"   && <ComplianceTab canEdit={perms.canEdit} />}
      {tab === "conflict"     && <ConflictOfInterestTab canEdit={perms.canEdit} records={conflicts} setRecords={setConflicts} />}
      {tab === "licenses"     && <LicensesTab canEdit={perms.canEdit} licenses={licenses} setLicenses={setLicenses} />}
      {tab === "satisfaction" && <SatisfactionTab />}
      {tab === "risks"        && <RisksTab canEdit={perms.canEdit} risks={risks} setRisks={setRisks} />}
      {tab === "audit"        && <AuditTab canEdit={perms.canEdit} audits={audits} setAudits={setAudits} />}
      {tab === "decisions"    && <DecisionsTab canEdit={perms.canEdit} />}
      {tab === "projects"     && <GovProjectsTab canEdit={perms.canEdit} projects={govProjects} setProjects={setGovProjects} />}
      {tab === "transactions" && <TransactionsTab budget={300000} module="governance" />}
      {tab === "meetings"     && <MeetingsTab departmentName="الحوكمة" module="governance" />}
      {tab === "reports"      && <ReportsTab departmentName="الحوكمة" />}
    </div>
  );
}

/* ─── Policies Tab ─── */
const INITIAL_POLICY = { title: "", category: "", number: "", summary: "", owner: "" };

function PoliciesTab({ canEdit, data, setData }) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_POLICY);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.title || !form.category || !form.summary) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    const payload = {
      number: form.number || `POL-${Date.now() % 1000}`,
      title: form.title,
      category: form.category,
      status: "draft",
      issue_date: today,
      last_update: today,
      owner: form.owner || "—",
      summary: form.summary,
      color: "#6366f1",
      key_points: [],
    };
    try {
      const created = await createGovPolicy(payload);
      setData(prev => [...prev, mapPolicyRow(created)]);
      setForm(INITIAL_POLICY);
      setShowAdd(false);
      showToast("تمت إضافة السياسة");
    } catch (e) {
      showToast(e.message || "تعذّر الحفظ", "error");
    }
  }

  const POL_STATUSES = [
    { key: "active", label: "سارية", color: C.success },
    { key: "review", label: "قيد المراجعة", color: C.warning },
    { key: "draft", label: "مسودة", color: "#2563eb" },
    { key: "archived", label: "مؤرشفة", color: C.textLight },
  ];

  const filtered = data.filter(p => p.title.includes(search) || p.category.includes(search));

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={S.kpiRow}>
        <KPICard title="إجمالي السياسات" value={data.length} icon={Scroll} color="#6366f1" />
        <KPICard title="سارية" value={data.filter(p => p.status === "active").length} icon={CheckCircle} color={C.success} />
        <KPICard title="قيد المراجعة" value={data.filter(p => p.status === "review").length} icon={Clock} color={C.warning} />
        <KPICard title="الفئات" value={[...new Set(data.map(p => p.category))].length} icon={Layers} color={C.teal} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={S.toolbar}>
          <div style={S.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث..." value={search} onChange={e => setSearch(e.target.value)} style={S.searchInput} />
          </div>
          {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة سياسة</Btn>}
        </div>
      </Card>

      <div style={S.policiesGrid}>
        {filtered.map(p => (
          <Card key={p.id} style={S.policyCard} onClick={() => setSelected(p)}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: `${p.color}12`, color: p.color }}>{p.category}</span>
              <StatusBadge status={p.status} statuses={POL_STATUSES} />
            </div>
            <div style={{ ...S.policyIcon, background: `${p.color}10` }}>
              <Scroll size={22} color={p.color} />
            </div>
            <h4 style={{ margin: "10px 0 4px", fontSize: 15, fontWeight: 700, color: C.primary }}>{p.title}</h4>
            <p style={{ margin: 0, fontSize: 12, color: C.textLight, lineHeight: 1.6 }}>{p.summary}</p>
            <div style={{ display: "flex", gap: 12, marginTop: 10, fontSize: 11, color: C.textLight }}>
              <span><Calendar size={11} /> {p.issueDate}</span>
              <span><Edit size={11} /> تحديث: {p.lastUpdate}</span>
            </div>
          </Card>
        ))}
      </div>

      {selected && (
        <Modal title={selected.title} onClose={() => setSelected(null)} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={S.detailRow}><strong>الفئة:</strong> {selected.category}</div>
            <div style={S.detailRow}><strong>رقم السياسة:</strong> {selected.number}</div>
            <div style={S.detailRow}><strong>تاريخ الإصدار:</strong> {selected.issueDate}</div>
            <div style={S.detailRow}><strong>آخر تحديث:</strong> {selected.lastUpdate}</div>
            <div style={S.detailRow}><strong>المسؤول:</strong> {selected.owner}</div>
            <div style={S.detailRow}><strong>الملخص:</strong><p style={{ margin: "4px 0 0", lineHeight: 1.8 }}>{selected.summary}</p></div>
            <div style={S.detailRow}>
              <strong>البنود الرئيسية:</strong>
              <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 6 }}>
                {selected.keyPoints.map((kp, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13 }}>
                    <CheckCircle size={14} color={C.teal} style={{ marginTop: 2, flexShrink: 0 }} />
                    <span>{kp}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="إضافة سياسة / لائحة" onClose={() => { setShowAdd(false); setForm(INITIAL_POLICY); }} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="العنوان" required placeholder="مثال: سياسة الشراء والمناقصات" value={form.title} onChange={v => setForm({ ...form, title: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الفئة" type="select" options={["مالية","إدارية","موارد بشرية","حوكمة","تقنية","تشغيلية"]} required value={form.category} onChange={v => setForm({ ...form, category: v })} />
              <Field label="رقم السياسة" placeholder="POL-XXX" value={form.number} onChange={v => setForm({ ...form, number: v })} />
            </div>
            <Field label="الملخص" type="textarea" required placeholder="ملخص السياسة..." rows={3} value={form.summary} onChange={v => setForm({ ...form, summary: v })} />
            <Field label="المسؤول" placeholder="الجهة المسؤولة" value={form.owner} onChange={v => setForm({ ...form, owner: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Scroll} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_POLICY); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─── Compliance Tab — درجة الحوكمة ─── */
const COMPLIANCE_STATUSES = ["مكتمل", "قيد التنفيذ", "متأخر"];
const COMPLIANCE_LABEL_TO_KEY = { "مكتمل": "done", "قيد التنفيذ": "pending", "متأخر": "overdue" };
const COMPLIANCE_CATEGORIES = ["تنظيمي", "مالي", "موارد بشرية", "تشغيلي", "تقني", "قانوني"];
const INITIAL_COMPLIANCE = { title: "", authority: "", category: "", deadline: "", statusLabel: "قيد التنفيذ", owner: "", notes: "" };

function ComplianceTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_COMPLIANCE);
  const [showToast, ToastView] = useToast();

  useEffect(() => {
    getGovCompliance()
      .then(rows => setItems((rows || []).map(mapComplianceRow)))
      .catch(() => {});
  }, []);

  async function handleSave() {
    if (!form.title || !form.authority || !form.deadline) {
      showToast("العنوان والجهة والموعد مطلوبون", "error");
      return;
    }
    const payload = {
      title: form.title,
      authority: form.authority,
      category: form.category || "تنظيمي",
      deadline: form.deadline,
      status: COMPLIANCE_LABEL_TO_KEY[form.statusLabel] || "pending",
      owner: form.owner || "—",
      notes: form.notes || "",
    };
    try {
      const created = await createGovCompliance(payload);
      setItems(prev => [mapComplianceRow(created), ...prev]);
      setForm(INITIAL_COMPLIANCE);
      setShowAdd(false);
      showToast("تمت إضافة متطلب الامتثال");
    } catch (e) {
      showToast(e?.message || "تعذّر إضافة متطلب الامتثال", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={S.kpiRow}>
        <KPICard title="درجة الحوكمة" value="0%" icon={ClipboardCheck} color={C.success} />
        <KPICard title="متطلبات مكتملة" value={items.filter(i => i.status === "done").length} icon={CheckCircle} color={C.teal} />
        <KPICard title="بانتظار التنفيذ" value={items.filter(i => i.status === "pending").length} icon={Clock} color={C.warning} />
        <KPICard title="متأخرة" value={items.filter(i => i.status === "overdue").length} icon={AlertCircle} color={C.danger} />
      </div>

      {canEdit && (
        <div style={{ marginBottom: 12, display: "flex", justifyContent: "flex-end" }}>
          <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">متطلب امتثال جديد</Btn>
        </div>
      )}

      <Card style={{ padding: 20 }}>
        <h4 style={S.sectionTitle}><ListChecks size={18} color={C.teal} /> متطلبات الحوكمة والامتثال</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {items.map((item, i) => (
            <div key={i} style={S.complianceRow}>
              <div style={{
                ...S.complianceIcon,
                background: item.status === "done" ? `${C.success}15` : item.status === "pending" ? `${C.warning}15` : `${C.danger}15`,
              }}>
                {item.status === "done" ? <CheckCircle size={18} color={C.success} /> :
                 item.status === "pending" ? <Clock size={18} color={C.warning} /> :
                 <AlertCircle size={18} color={C.danger} />}
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.primary }}>{item.title}</p>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: C.textLight }}>{item.authority} — {item.deadline}</p>
              </div>
              <span style={{
                padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                background: item.status === "done" ? `${C.success}15` : item.status === "pending" ? `${C.warning}15` : `${C.danger}15`,
                color: item.status === "done" ? C.success : item.status === "pending" ? C.warning : C.danger,
              }}>
                {item.status === "done" ? "مكتمل" : item.status === "pending" ? "قيد التنفيذ" : "متأخر"}
              </span>
            </div>
          ))}
        </div>
      </Card>

      {/* Compliance score breakdown */}
      <Card style={{ padding: 20, marginTop: 16 }}>
        <h4 style={S.sectionTitle}><Shield size={18} color="#6366f1" /> نسبة الامتثال حسب المجال</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {COMPLIANCE_AREAS.map((a, i) => (
            <div key={i} style={S.areaRow}>
              <span style={{ fontSize: 13, fontWeight: 600, color: C.primary, width: 150 }}>{a.name}</span>
              <div style={{ flex: 1 }}>
                <div style={S.areaBarBg}>
                  <div style={{ height: "100%", borderRadius: 4, width: `${a.score}%`, background: a.score >= 90 ? C.success : a.score >= 70 ? C.teal : C.warning }} />
                </div>
              </div>
              <span style={{ fontSize: 14, fontWeight: 700, color: a.score >= 90 ? C.success : a.score >= 70 ? C.teal : C.warning, minWidth: 45 }}>{a.score}%</span>
            </div>
          ))}
        </div>
      </Card>

      {showAdd && (
        <Modal title="إضافة متطلب امتثال" onClose={() => { setShowAdd(false); setForm(INITIAL_COMPLIANCE); }} width={620}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="عنوان المتطلب" required placeholder="مثال: تقرير الحوكمة الربع سنوي" value={form.title} onChange={v => setForm({ ...form, title: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="الجهة المختصة" required placeholder="مثال: مجلس الإدارة" value={form.authority} onChange={v => setForm({ ...form, authority: v })} />
              <Field label="التصنيف" type="select" options={COMPLIANCE_CATEGORIES} value={form.category} onChange={v => setForm({ ...form, category: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label="الموعد النهائي" required type="date" value={form.deadline} onChange={v => setForm({ ...form, deadline: v })} />
              <Field label="الحالة" type="select" options={COMPLIANCE_STATUSES} value={form.statusLabel} onChange={v => setForm({ ...form, statusLabel: v })} />
              <Field label="المسؤول" placeholder="اسم المسؤول" value={form.owner} onChange={v => setForm({ ...form, owner: v })} />
            </div>
            <Field label="ملاحظات" type="textarea" placeholder="تفاصيل إضافية..." rows={2} value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={ClipboardCheck} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_COMPLIANCE); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─── Risks Tab ─── */
const INITIAL_RISK = { title: "", category: "", levelLabel: "متوسط", probability: "متوسط", mitigation: "", owner: "" };

function RisksTab({ canEdit, risks, setRisks }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_RISK);
  const [showToast, ToastView] = useToast();

  const levelLabelToKey = { "حرج": "critical", "مرتفع": "high", "متوسط": "medium", "منخفض": "low" };

  async function handleSave() {
    if (!form.title || !form.category || !form.levelLabel || !form.probability) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const num = String(risks.length + 1).padStart(3, "0");
    const payload = {
      code: `R-${num}`,
      title: form.title,
      category: form.category,
      level: levelLabelToKey[form.levelLabel] || "medium",
      impact: form.levelLabel === "حرج" || form.levelLabel === "مرتفع" ? "عالي" : form.levelLabel === "متوسط" ? "متوسط" : "منخفض",
      probability: form.probability,
      mitigation: form.mitigation ? "جارية" : "—",
    };
    try {
      const created = await createGovRisk(payload);
      setRisks(prev => [...prev, mapRiskRow(created)]);
      setForm(INITIAL_RISK);
      setShowAdd(false);
      showToast("تمت إضافة الخطر");
    } catch (e) {
      showToast(e.message || "تعذّر الحفظ", "error");
    }
  }

  const RISK_LEVELS = [
    { key: "critical", label: "حرج", color: C.danger },
    { key: "high", label: "مرتفع", color: "#ea580c" },
    { key: "medium", label: "متوسط", color: C.warning },
    { key: "low", label: "منخفض", color: C.success },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={S.kpiRow}>
        <KPICard title="المخاطر المحددة" value={risks.length} icon={AlertTriangle} color="#ea580c" />
        <KPICard title="حرجة" value={risks.filter(r => r.level === "critical").length} icon={XCircle} color={C.danger} />
        <KPICard title="مرتفعة" value={risks.filter(r => r.level === "high").length} icon={AlertTriangle} color="#ea580c" />
        <KPICard title="تحت المعالجة" value={risks.filter(r => r.mitigation === "جارية").length} icon={Clock} color={C.teal} />
      </div>

      {canEdit && (
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
          <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة خطر</Btn>
        </div>
      )}

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "code", label: "الرمز", width: "80px", render: v => <span style={{ fontWeight: 600, fontFamily: "monospace" }}>{v}</span> },
            { key: "title", label: "وصف الخطر", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "category", label: "الفئة", width: "100px" },
            { key: "level", label: "المستوى", width: "90px", render: v => <StatusBadge status={v} statuses={RISK_LEVELS} /> },
            { key: "impact", label: "الأثر", width: "80px", render: v => (
              <span style={{ fontWeight: 600, color: v === "عالي" ? C.danger : v === "متوسط" ? C.warning : C.success }}>{v}</span>
            )},
            { key: "probability", label: "الاحتمال", width: "80px" },
            { key: "mitigation", label: "المعالجة", width: "90px", render: v => (
              <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: v === "مكتملة" ? `${C.success}15` : `${C.teal}15`, color: v === "مكتملة" ? C.success : C.teal }}>{v}</span>
            )},
          ]}
          data={risks}
        />
      </Card>

      {showAdd && (
        <Modal title="إضافة خطر" onClose={() => { setShowAdd(false); setForm(INITIAL_RISK); }} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="وصف الخطر" required placeholder="وصف مختصر للخطر" value={form.title} onChange={v => setForm({ ...form, title: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="الفئة" type="select" options={["مالي","تشغيلي","قانوني","سمعة","تقني","بشري"]} required value={form.category} onChange={v => setForm({ ...form, category: v })} />
              <Field label="المستوى" type="select" options={["حرج","مرتفع","متوسط","منخفض"]} required value={form.levelLabel} onChange={v => setForm({ ...form, levelLabel: v })} />
              <Field label="الاحتمال" type="select" options={["مرتفع","متوسط","منخفض"]} required value={form.probability} onChange={v => setForm({ ...form, probability: v })} />
            </div>
            <Field label="خطة المعالجة" type="textarea" placeholder="الإجراءات المطلوبة للتخفيف من الخطر..." rows={3} value={form.mitigation} onChange={v => setForm({ ...form, mitigation: v })} />
            <Field label="المسؤول" placeholder="الجهة المسؤولة" value={form.owner} onChange={v => setForm({ ...form, owner: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={AlertTriangle} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_RISK); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─── Audit Tab ─── */
const INITIAL_AUDIT = { title: "", scope: "", auditor: "", date: "", notes: "" };

function AuditTab({ canEdit, audits, setAudits }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_AUDIT);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.title || !form.scope || !form.date) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const num = String(audits.length + 1).padStart(3, "0");
    const payload = {
      number: `AUD-${num}`,
      title: form.title,
      scope: form.scope,
      auditor: form.auditor || "—",
      audit_date: form.date,
      findings: 0,
      open_findings: 0,
      status: "planned",
    };
    try {
      const created = await createGovAudit(payload);
      setAudits(prev => [...prev, mapAuditRow(created)]);
      setForm(INITIAL_AUDIT);
      setShowAdd(false);
      showToast("تم إنشاء عملية التدقيق");
    } catch (e) {
      showToast(e.message || "تعذّر الحفظ", "error");
    }
  }

  const AUDIT_STATUSES = [
    { key: "completed", label: "مكتمل", color: C.success },
    { key: "in_progress", label: "جاري", color: C.teal },
    { key: "planned", label: "مخطط", color: C.warning },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={S.kpiRow}>
        <KPICard title="عمليات التدقيق" value={audits.length} icon={ShieldCheck} color="#6366f1" />
        <KPICard title="مكتملة" value={audits.filter(a => a.status === "completed").length} icon={CheckCircle} color={C.success} />
        <KPICard title="ملاحظات مفتوحة" value={audits.reduce((s, a) => s + a.openFindings, 0)} icon={AlertCircle} color={C.warning} />
      </div>

      {canEdit && (
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
          <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">تدقيق جديد</Btn>
        </div>
      )}

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "number", label: "الرقم", width: "80px", render: v => <span style={{ fontWeight: 600, fontFamily: "monospace" }}>{v}</span> },
            { key: "title", label: "العنوان", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "scope", label: "النطاق", width: "120px" },
            { key: "auditor", label: "المدقق", width: "130px" },
            { key: "date", label: "التاريخ", width: "100px" },
            { key: "findings", label: "الملاحظات", width: "80px", render: v => <span style={{ fontWeight: 700, color: C.primary }}>{v}</span> },
            { key: "openFindings", label: "مفتوحة", width: "70px", render: v => <span style={{ fontWeight: 700, color: v > 0 ? C.warning : C.success }}>{v}</span> },
            { key: "status", label: "الحالة", width: "90px", render: v => <StatusBadge status={v} statuses={AUDIT_STATUSES} /> },
          ]}
          data={audits}
        />
      </Card>

      {showAdd && (
        <Modal title="إنشاء عملية تدقيق" onClose={() => { setShowAdd(false); setForm(INITIAL_AUDIT); }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="العنوان" required placeholder="مثال: تدقيق مالي ربع سنوي" value={form.title} onChange={v => setForm({ ...form, title: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="النطاق" type="select" options={["مالي","تشغيلي","إداري","تقني","شامل"]} required value={form.scope} onChange={v => setForm({ ...form, scope: v })} />
              <Field label="المدقق" placeholder="اسم المدقق" value={form.auditor} onChange={v => setForm({ ...form, auditor: v })} />
            </div>
            <Field label="التاريخ" type="date" required value={form.date} onChange={v => setForm({ ...form, date: v })} />
            <Field label="ملاحظات" type="textarea" placeholder="ملاحظات إضافية..." value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={ShieldCheck} onClick={handleSave}>إنشاء</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_AUDIT); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─── Governance Overview Tab ─── */
function GovOverviewTab() {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={S.kpiRow}>
        <KPICard title="درجة الحوكمة" value="0%" icon={ShieldCheck} color={C.success} />
        <KPICard title="اللوائح النشطة" value={STRATEGIC_DATA.policiesActive} icon={Scroll} color="#6366f1" />
        <KPICard title="القوائم المالية" value={STRATEGIC_DATA.financialsApproved} icon={DollarSign} color={C.teal} />
        <KPICard title="التراخيص" value={STRATEGIC_DATA.activeLicenses} icon={Receipt} color={C.accent} />
      </div>

      <Card style={{ padding: 22, marginBottom: 20 }}>
        <h4 style={S.sectionTitle}>
          <Target size={18} color="#6366f1" /> الأهداف الاستراتيجية الكبرى
        </h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {STRATEGIC_GOALS.map(g => {
            const pct = Math.round((g.current / g.target) * 100);
            return (
              <div key={g.id}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: C.primary, flex: 1 }}>{g.title}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: pct >= 80 ? C.success : pct >= 50 ? C.teal : C.warning, minWidth: 110, textAlign: "left" }}>
                    {g.current.toLocaleString()} / {g.target.toLocaleString()} ({pct}%)
                  </span>
                </div>
                <div style={{ height: 12, background: C.bg, borderRadius: 6, overflow: "hidden" }}>
                  <div style={{
                    height: "100%", borderRadius: 6, width: `${Math.min(pct, 100)}%`,
                    background: pct >= 80 ? C.success : pct >= 50 ? C.teal : C.warning,
                    transition: "width 0.4s",
                  }} />
                </div>
                {g.note && <p style={{ margin: "4px 0 0", fontSize: 11, color: C.textLight }}>{g.note}</p>}
              </div>
            );
          })}
        </div>
      </Card>

      <Card style={{ padding: 22 }}>
        <h4 style={S.sectionTitle}>
          <Gavel size={18} color="#7c3aed" /> القرارات المتأخرة والجارية
        </h4>
        <DataTable
          columns={[
            { key: "number", label: "الرقم", width: "110px", render: v => <span style={{ fontWeight: 700, fontFamily: "monospace" }}>{v}</span> },
            { key: "title", label: "العنوان" },
            { key: "responsible", label: "المسؤول", width: "150px" },
            { key: "deadline", label: "الموعد النهائي", width: "120px" },
            { key: "status", label: "الحالة", width: "110px", render: v => (
              <span style={{
                padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                background: v === "متأخر" ? `${C.danger}15` : `${C.warning}15`,
                color: v === "متأخر" ? C.danger : C.warning,
              }}>{v}</span>
            )},
          ]}
          data={OVERDUE_DECISIONS}
        />
      </Card>
    </div>
  );
}

/* ─── Strategic Plan Tab ─── */
const INITIAL_STRAT_GOAL = {
  name: "", description: "",
  target: "", current: "",
  periodFrom: "", periodTo: "",
  owner: "", indicators: "", risks: "",
  priority: "عالية", budget: "",
};

function mapGovGoalRow(r) {
  return {
    id: r.id,
    title: r.title || "",
    description: r.description || "",
    target: Number(r.target) || 0,
    current: Number(r.current) || 0,
    periodFrom: r.period_from || "",
    periodTo: r.period_to || "",
    owner: r.owner || "",
    indicators: r.indicators || "",
    risks: r.risks || "",
    priority: r.priority || "",
    budget: Number(r.budget) || 0,
    note: r.note || "",
  };
}

function StrategicPlanTab() {
  const [goals, setGoals] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_STRAT_GOAL);
  const [showToast, ToastView] = useToast();

  useEffect(() => {
    getGovGoals()
      .then(rows => setGoals((rows || []).map(mapGovGoalRow)))
      .catch(() => {});
  }, []);

  async function handleSave() {
    if (!form.name || !form.target) {
      showToast("اسم الهدف والقيمة المستهدفة مطلوبة", "error");
      return;
    }
    const payload = {
      title: form.name,
      description: form.description,
      target: Number(form.target) || 0,
      current: Number(form.current) || 0,
      period_from: form.periodFrom || null,
      period_to: form.periodTo || null,
      owner: form.owner,
      indicators: form.indicators,
      risks: form.risks,
      priority: form.priority,
      budget: Number(form.budget) || 0,
      note: form.description,
    };
    try {
      const created = await createGovGoal(payload);
      setGoals(prev => [mapGovGoalRow(created), ...prev]);
      setForm(INITIAL_STRAT_GOAL);
      setShowAdd(false);
      showToast("تمت إضافة الهدف الاستراتيجي");
    } catch (e) {
      showToast(e?.message || "تعذّر إضافة الهدف", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.primary }}>الأهداف الاستراتيجية الكبرى</h3>
        <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة هدف استراتيجي</Btn>
      </div>

      <Card style={{ padding: 22 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          {goals.map(g => {
            const pct = Math.round((g.current / g.target) * 100);
            return (
              <div key={g.id} style={{ paddingBottom: 18, borderBottom: `1px solid ${C.borderLight}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.primary, flex: 1 }}>{g.title}</h4>
                  <span style={{ fontSize: 14, fontWeight: 700, color: pct >= 80 ? C.success : pct >= 50 ? C.teal : C.warning, minWidth: 130, textAlign: "left" }}>
                    {g.current.toLocaleString()} / {g.target.toLocaleString()} ({pct}%)
                  </span>
                </div>
                <div style={{ height: 12, background: C.bg, borderRadius: 6, overflow: "hidden", marginBottom: 8 }}>
                  <div style={{ height: "100%", borderRadius: 6, width: `${Math.min(pct, 100)}%`, background: pct >= 80 ? C.success : pct >= 50 ? C.teal : C.warning, transition: "width 0.4s" }} />
                </div>
                {g.note && <p style={{ margin: 0, fontSize: 13, color: C.textLight, lineHeight: 1.7 }}>{g.note}</p>}
                {(g.owner || g.indicators || g.risks) && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 10, fontSize: 12 }}>
                    {g.owner && <div><strong style={{ color: C.textLight }}>المسؤول:</strong> {g.owner}</div>}
                    {g.indicators && <div><strong style={{ color: C.textLight }}>المؤشرات:</strong> {g.indicators}</div>}
                    {g.risks && <div><strong style={{ color: C.danger }}>المخاطر:</strong> {g.risks}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {showAdd && (
        <Modal title="إضافة هدف استراتيجي" onClose={() => { setShowAdd(false); setForm(INITIAL_STRAT_GOAL); }} width={680}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="اسم الهدف" required placeholder="مثال: زيادة المستفيدين بنسبة 50%" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <Field label="الوصف" type="textarea" rows={2} placeholder="وصف موجز للهدف..." value={form.description} onChange={v => setForm({ ...form, description: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="القيمة المستهدفة" type="number" required placeholder="مثال: 1500" value={form.target} onChange={v => setForm({ ...form, target: v })} />
              <Field label="القيمة الحالية" type="number" placeholder="مثال: 1100" value={form.current} onChange={v => setForm({ ...form, current: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="من تاريخ" type="date" value={form.periodFrom} onChange={v => setForm({ ...form, periodFrom: v })} />
              <Field label="إلى تاريخ" type="date" value={form.periodTo} onChange={v => setForm({ ...form, periodTo: v })} />
            </div>
            <Field label="المسؤول" placeholder="اسم المسؤول عن الهدف" value={form.owner} onChange={v => setForm({ ...form, owner: v })} />
            <Field label="المؤشرات" type="textarea" rows={2} placeholder="مؤشرات قياس تحقق الهدف..." value={form.indicators} onChange={v => setForm({ ...form, indicators: v })} />
            <Field label="المخاطر المتوقعة" type="textarea" rows={2} placeholder="المخاطر المحتملة..." value={form.risks} onChange={v => setForm({ ...form, risks: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الأولوية" type="select" options={["عالية", "متوسطة", "منخفضة"]} value={form.priority} onChange={v => setForm({ ...form, priority: v })} />
              <Field label="الميزانية (ر.س)" type="number" placeholder="0" value={form.budget} onChange={v => setForm({ ...form, budget: v })} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Target} onClick={handleSave}>حفظ الهدف</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_STRAT_GOAL); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─── Governance Projects Tab ─── */
const INITIAL_GOV_PROJECT = { name: "", description: "", owner: "", startDate: "", endDate: "", progress: "", status: "in_progress" };
const GOV_PROJECT_STATUSES = [
  { key: "in_progress", label: "جاري", color: C.teal },
  { key: "completed",   label: "مكتمل", color: C.success },
  { key: "delayed",     label: "متأخر", color: C.warning },
  { key: "planned",     label: "مخطط", color: "#6366f1" },
];
const GOV_PROJECT_STATUS_OPTIONS = ["جاري", "مكتمل", "متأخر", "مخطط"];
const GOV_PROJECT_LABEL_TO_KEY = { "جاري": "in_progress", "مكتمل": "completed", "متأخر": "delayed", "مخطط": "planned" };

function GovProjectsTab({ canEdit, projects, setProjects }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_GOV_PROJECT);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.name) {
      showToast("اسم المشروع مطلوب", "error");
      return;
    }
    const payload = {
      name: form.name,
      description: form.description,
      owner: form.owner || "—",
      start_date: form.startDate,
      end_date: form.endDate,
      progress: Math.min(100, Math.max(0, Number(form.progress) || 0)),
      status: GOV_PROJECT_LABEL_TO_KEY[form.status] || "in_progress",
    };
    try {
      const created = await createGovProject(payload);
      setProjects(prev => [...prev, mapProjectRow(created)]);
      setForm(INITIAL_GOV_PROJECT);
      setShowAdd(false);
      showToast("تمت إضافة المشروع");
    } catch (e) {
      showToast(e.message || "تعذّر الحفظ", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}

      <div style={S.kpiRow}>
        <KPICard title="إجمالي المشروعات" value={projects.length} icon={Rocket} color="#6366f1" />
        <KPICard title="جارية" value={projects.filter(p => p.status === "in_progress").length} icon={Clock} color={C.teal} />
        <KPICard title="مكتملة" value={projects.filter(p => p.status === "completed").length} icon={CheckCircle} color={C.success} />
        <KPICard title="متأخرة" value={projects.filter(p => p.status === "delayed").length} icon={AlertTriangle} color={C.warning} />
      </div>

      {canEdit && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة مشروع</Btn>
        </div>
      )}

      <div style={{ display: "grid", gap: 14 }}>
        {projects.map(p => (
          <Card key={p.id}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <div>
                <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.primary }}>{p.name}</h4>
                <p style={{ margin: "4px 0 0", fontSize: 13, color: C.textLight, lineHeight: 1.6 }}>{p.description}</p>
              </div>
              <StatusBadge status={p.status} statuses={GOV_PROJECT_STATUSES} />
            </div>
            <div style={{ display: "flex", gap: 16, fontSize: 12, color: C.textLight, marginBottom: 10, flexWrap: "wrap" }}>
              {p.owner && <span>المسؤول: <strong style={{ color: C.primary }}>{p.owner}</strong></span>}
              {p.startDate && <span>البداية: {p.startDate}</span>}
              {p.endDate && <span>النهاية: {p.endDate}</span>}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.textLight }}>التقدم</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: p.progress >= 80 ? C.success : p.progress >= 50 ? C.teal : C.warning }}>{p.progress}%</span>
            </div>
            <div style={{ height: 10, background: C.bg, borderRadius: 5, overflow: "hidden" }}>
              <div style={{ height: "100%", borderRadius: 5, width: `${p.progress}%`, background: p.progress >= 80 ? C.success : p.progress >= 50 ? C.teal : C.warning, transition: "width 0.4s" }} />
            </div>
          </Card>
        ))}
      </div>

      {showAdd && (
        <Modal title="إضافة مشروع حوكمة" onClose={() => { setShowAdd(false); setForm(INITIAL_GOV_PROJECT); }} width={620}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="اسم المشروع" required placeholder="مثال: تطوير لوائح الحوكمة" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <Field label="الوصف" type="textarea" rows={3} placeholder="وصف المشروع وأهدافه..." value={form.description} onChange={v => setForm({ ...form, description: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="تاريخ البداية" type="date" value={form.startDate} onChange={v => setForm({ ...form, startDate: v })} />
              <Field label="تاريخ النهاية" type="date" value={form.endDate} onChange={v => setForm({ ...form, endDate: v })} />
              <Field label="نسبة التقدم (%)" type="number" placeholder="0" value={form.progress} onChange={v => setForm({ ...form, progress: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="المسؤول" placeholder="اسم المسؤول" value={form.owner} onChange={v => setForm({ ...form, owner: v })} />
              <Field label="الحالة" type="select" options={GOV_PROJECT_STATUS_OPTIONS} value={form.status} onChange={v => setForm({ ...form, status: v })} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Rocket} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_GOV_PROJECT); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─── Financials Tab — القوائم المالية ─── */
const FIN_STATEMENT_TYPES = ["ميزانية عمومية", "قائمة الدخل", "التدفقات النقدية", "تغيرات حقوق الملكية", "إيضاحات"];
const FIN_STATEMENT_STATUSES = ["مسودة", "مراجعة", "معتمدة"];
const INITIAL_FIN_STATEMENT = { name: "", type: "ميزانية عمومية", period: "", status: "مسودة", total: "", notes: "" };

function FinancialsTab({ statements, setStatements }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_FIN_STATEMENT);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.name || !form.type || !form.period) {
      showToast("الاسم والنوع والفترة مطلوبة", "error");
      return;
    }
    const payload = {
      name: form.name,
      type: form.type,
      period: form.period,
      status: form.status,
      total: Number(form.total) || 0,
      notes: form.notes,
    };
    try {
      const created = await createGovFinStatement(payload);
      setStatements(prev => [mapFinRow(created), ...prev]);
      setForm(INITIAL_FIN_STATEMENT);
      setShowAdd(false);
      showToast("تمت إضافة القائمة المالية");
    } catch (e) {
      showToast(e.message || "تعذّر الحفظ", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.primary }}>القوائم المالية</h3>
        <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة قائمة مالية</Btn>
      </div>

      <div style={S.kpiRow}>
        <KPICard title="إجمالي الأصول" value="0" icon={Building} color={C.primary} subtitle="ر.س" />
        <KPICard title="إجمالي الالتزامات" value="0" icon={FileText} color={C.warning} subtitle="ر.س" />
        <KPICard title="صافي الأصول" value="0" icon={TrendingUp} color={C.success} subtitle="ر.س" />
        <KPICard title="السنة المالية" value="1446هـ" icon={Calendar} color={C.teal} />
      </div>

      {/* Statements list */}
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <h4 style={S.sectionTitle}>قائمة القوائم المالية المعتمدة</h4>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
          {statements.map(s => (
            <div key={s.id} style={{ padding: 12, background: C.bg, borderRadius: 10, border: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <strong style={{ fontSize: 13, color: C.primary }}>{s.name}</strong>
                <span style={{
                  padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700,
                  background: s.status === "معتمدة" ? `${C.success}15` : s.status === "مراجعة" ? `${C.warning}15` : `${C.textLight}15`,
                  color: s.status === "معتمدة" ? C.success : s.status === "مراجعة" ? C.warning : C.textLight,
                }}>{s.status}</span>
              </div>
              <p style={{ margin: "2px 0", fontSize: 12, color: C.textLight }}>{s.type} — {s.period}</p>
              {s.total > 0 && <p style={{ margin: "4px 0 0", fontSize: 13, fontWeight: 700, color: C.primary }}>{s.total.toLocaleString()} ر.س</p>}
            </div>
          ))}
        </div>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
        {/* Balance Sheet */}
        <Card style={{ padding: 20 }}>
          <h4 style={S.sectionTitle}><Building size={18} color={C.primary} /> الميزانية العمومية</h4>
          {BALANCE_SHEET.map((item, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < BALANCE_SHEET.length - 1 ? `1px solid ${C.bg}` : "none" }}>
              <span style={{ fontSize: 13, color: item.bold ? C.primary : C.textLight, fontWeight: item.bold ? 700 : 400 }}>{item.label}</span>
              <span style={{ fontSize: 13, fontWeight: item.bold ? 700 : 600, color: item.bold ? C.primary : C.text }}>{item.value.toLocaleString()} ر.س</span>
            </div>
          ))}
        </Card>

        {/* Income Statement */}
        <Card style={{ padding: 20 }}>
          <h4 style={S.sectionTitle}><TrendingUp size={18} color={C.success} /> قائمة الدخل</h4>
          {INCOME_STATEMENT.map((item, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < INCOME_STATEMENT.length - 1 ? `1px solid ${C.bg}` : "none" }}>
              <span style={{ fontSize: 13, color: item.bold ? C.primary : C.textLight, fontWeight: item.bold ? 700 : 400 }}>{item.label}</span>
              <span style={{ fontSize: 13, fontWeight: item.bold ? 700 : 600, color: item.value < 0 ? C.danger : C.success }}>
                {item.value < 0 ? "−" : ""}{Math.abs(item.value).toLocaleString()} ر.س
              </span>
            </div>
          ))}
        </Card>

        {/* Cash Flow */}
        <Card style={{ padding: 20 }}>
          <h4 style={S.sectionTitle}><DollarSign size={18} color={C.teal} /> التدفقات النقدية</h4>
          {CASH_FLOW.map((item, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < CASH_FLOW.length - 1 ? `1px solid ${C.bg}` : "none" }}>
              <span style={{ fontSize: 13, color: item.bold ? C.primary : C.textLight, fontWeight: item.bold ? 700 : 400 }}>{item.label}</span>
              <span style={{ fontSize: 13, fontWeight: item.bold ? 700 : 600, color: item.value < 0 ? C.danger : C.success }}>
                {item.value < 0 ? "−" : ""}{Math.abs(item.value).toLocaleString()} ر.س
              </span>
            </div>
          ))}
        </Card>
      </div>

      {showAdd && (
        <Modal title="إضافة قائمة مالية" onClose={() => { setShowAdd(false); setForm(INITIAL_FIN_STATEMENT); }} width={580}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="اسم القائمة" required placeholder="مثال: ميزانية عمومية 1446" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="النوع" type="select" options={FIN_STATEMENT_TYPES} value={form.type} onChange={v => setForm({ ...form, type: v })} />
              <Field label="الفترة" placeholder="مثال: 1446 / Q1 1447" value={form.period} onChange={v => setForm({ ...form, period: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الحالة" type="select" options={FIN_STATEMENT_STATUSES} value={form.status} onChange={v => setForm({ ...form, status: v })} />
              <Field label="الإجمالي (ر.س)" type="number" placeholder="0" value={form.total} onChange={v => setForm({ ...form, total: v })} />
            </div>
            <Field label="ملاحظات" type="textarea" rows={2} placeholder="ملاحظات إضافية..." value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={DollarSign} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_FIN_STATEMENT); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─── Conflict of Interest Tab — تضارب المصالح ─── */
const INITIAL_CONFLICT = { member: "", nature: "", action: "", date: "" };

function ConflictOfInterestTab({ canEdit, records, setRecords }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_CONFLICT);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.member || !form.nature || !form.action || !form.date) {
      showToast("جميع الحقول مطلوبة", "error");
      return;
    }
    const payload = {
      member: form.member,
      nature: form.nature,
      action: form.action,
      conflict_date: form.date,
    };
    try {
      const created = await createGovConflict(payload);
      setRecords(prev => [mapConflictRow(created), ...prev]);
      setForm(INITIAL_CONFLICT);
      setShowAdd(false);
      showToast("تم تسجيل الإفصاح");
    } catch (e) {
      showToast(e.message || "تعذّر الحفظ", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}

      <div style={S.kpiRow}>
        <KPICard title="إجمالي الإفصاحات" value={records.length} icon={FileWarning} color="#ea580c" />
        <KPICard title="هذا العام" value={records.filter(r => (r.date || "").startsWith(String(new Date().getFullYear()))).length} icon={Calendar} color={C.teal} />
      </div>

      <div style={{ padding: 12, marginBottom: 16, background: `${C.teal}08`, borderRadius: 8, fontSize: 13, color: C.primary, display: "flex", alignItems: "center", gap: 8 }}>
        <FileWarning size={16} color={C.teal} />
        نموذج الإفصاح عن تضارب المصالح لأعضاء مجلس الإدارة والإدارة التنفيذية.
      </div>

      {canEdit && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إفصاح جديد</Btn>
        </div>
      )}

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "member", label: "اسم العضو", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "nature", label: "طبيعة التضارب" },
            { key: "action", label: "الإجراء المتخذ" },
            { key: "date", label: "التاريخ", width: "110px" },
          ]}
          data={records}
        />
      </Card>

      {showAdd && (
        <Modal title="إفصاح تضارب مصالح" onClose={() => { setShowAdd(false); setForm(INITIAL_CONFLICT); }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="اسم العضو" required placeholder="اسم العضو المفصح" value={form.member} onChange={v => setForm({ ...form, member: v })} />
            <Field label="طبيعة التضارب" type="textarea" required placeholder="وصف طبيعة تضارب المصالح..." rows={3} value={form.nature} onChange={v => setForm({ ...form, nature: v })} />
            <Field label="الإجراء المتخذ" type="textarea" required placeholder="الإجراءات المتخذة (انسحاب من التصويت، تقديم استشارة، إلخ)" rows={3} value={form.action} onChange={v => setForm({ ...form, action: v })} />
            <Field label="التاريخ" type="date" required value={form.date} onChange={v => setForm({ ...form, date: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={FileWarning} onClick={handleSave}>حفظ الإفصاح</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_CONFLICT); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─── Licenses Tab — تراخيص جمع التبرعات ─── */
const LICENSE_STATUSES = [
  { key: "active", label: "ساري", color: C.success },
  { key: "expiring", label: "قارب الانتهاء", color: C.warning },
  { key: "expired", label: "منتهي", color: C.danger },
];
const LICENSE_STATUS_OPTIONS = ["ساري", "قارب الانتهاء", "منتهي"];
const LICENSE_LABEL_TO_KEY = { "ساري": "active", "قارب الانتهاء": "expiring", "منتهي": "expired" };

const INITIAL_LICENSE = { number: "", purpose: "", authority: "", issueDate: "", expiryDate: "", statusLabel: "ساري" };

function LicensesTab({ canEdit, licenses, setLicenses }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_LICENSE);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.number || !form.purpose || !form.authority || !form.issueDate || !form.expiryDate) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const payload = {
      number: form.number,
      purpose: form.purpose,
      authority: form.authority,
      issue_date: form.issueDate,
      expiry_date: form.expiryDate,
      status: LICENSE_LABEL_TO_KEY[form.statusLabel] || "active",
    };
    try {
      const created = await createGovLicense(payload);
      setLicenses(prev => [mapLicenseRow(created), ...prev]);
      setForm(INITIAL_LICENSE);
      setShowAdd(false);
      showToast("تم حفظ الترخيص");
    } catch (e) {
      showToast(e.message || "تعذّر الحفظ", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}

      <div style={S.kpiRow}>
        <KPICard title="إجمالي التراخيص" value={licenses.length} icon={Receipt} color={C.accent} />
        <KPICard title="سارية" value={licenses.filter(l => l.status === "active").length} icon={CheckCircle} color={C.success} />
        <KPICard title="قاربت الانتهاء" value={licenses.filter(l => l.status === "expiring").length} icon={Clock} color={C.warning} />
        <KPICard title="منتهية" value={licenses.filter(l => l.status === "expired").length} icon={XCircle} color={C.danger} />
      </div>

      {canEdit && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة ترخيص</Btn>
        </div>
      )}

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "number", label: "رقم الترخيص", width: "140px", render: v => <span style={{ fontWeight: 600, fontFamily: "monospace" }}>{v}</span> },
            { key: "purpose", label: "الغرض" },
            { key: "authority", label: "الجهة المُصدرة", width: "200px" },
            { key: "issueDate", label: "تاريخ الإصدار", width: "120px" },
            { key: "expiryDate", label: "تاريخ الانتهاء", width: "120px" },
            { key: "status", label: "الحالة", width: "120px", render: v => <StatusBadge status={v} statuses={LICENSE_STATUSES} /> },
          ]}
          data={licenses}
        />
      </Card>

      {showAdd && (
        <Modal title="إضافة ترخيص" onClose={() => { setShowAdd(false); setForm(INITIAL_LICENSE); }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="رقم الترخيص" required placeholder="LIC-2026-001" value={form.number} onChange={v => setForm({ ...form, number: v })} />
            <Field label="الغرض" required placeholder="مثال: حملة رمضان السنوية" value={form.purpose} onChange={v => setForm({ ...form, purpose: v })} />
            <Field label="الجهة المُصدرة" required placeholder="مثال: المركز الوطني لتنمية القطاع غير الربحي" value={form.authority} onChange={v => setForm({ ...form, authority: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="تاريخ الإصدار" type="date" required value={form.issueDate} onChange={v => setForm({ ...form, issueDate: v })} />
              <Field label="تاريخ الانتهاء" type="date" required value={form.expiryDate} onChange={v => setForm({ ...form, expiryDate: v })} />
              <Field label="الحالة" type="select" options={LICENSE_STATUS_OPTIONS} value={form.statusLabel} onChange={v => setForm({ ...form, statusLabel: v })} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Receipt} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_LICENSE); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─── Satisfaction Tab — نتائج قياس الرضا ─── */
const SAT_AUDIENCES = ["مستفيدون", "متبرعون", "موظفون", "متطوعون"];
const INITIAL_SAT = { title: "", audience: "مستفيدون", periodFrom: "", periodTo: "", score: "", responses: "", note: "" };

function mapGovSurveyRow(r) {
  return {
    id: r.id,
    title: r.title || "",
    audience: r.audience || "",
    score: Number(r.score) || 0,
    responses: Number(r.responses) || 0,
    date: r.date || "",
    period: r.period || "",
    note: r.note || "",
  };
}

function SatisfactionTab() {
  const [surveys, setSurveys] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_SAT);
  const [showToast, ToastView] = useToast();

  useEffect(() => {
    getGovSurveys()
      .then(rows => setSurveys((rows || []).map(mapGovSurveyRow)))
      .catch(() => {});
  }, []);

  const filterByAudience = (aud) => surveys.filter(s => s.audience === aud);
  const calcAvg = (list) => list.length ? Math.round(list.reduce((sum, s) => sum + s.score, 0) / list.length) : 0;
  const avgBeneficiaries = calcAvg(filterByAudience("مستفيدون"));
  const avgDonors = calcAvg(filterByAudience("متبرعون"));
  const avgVolunteers = calcAvg(filterByAudience("متطوعون"));
  const overallAvg = calcAvg(surveys);

  async function handleSave() {
    if (!form.title || !form.score) {
      showToast("العنوان والنسبة مطلوبان", "error");
      return;
    }
    const payload = {
      title: form.title,
      audience: form.audience,
      score: Number(form.score) || 0,
      responses: Number(form.responses) || 0,
      date: form.periodTo || new Date().toISOString().slice(0, 10),
      period: form.periodFrom && form.periodTo ? `${form.periodFrom} → ${form.periodTo}` : "",
      note: form.note,
    };
    try {
      const created = await createGovSurvey(payload);
      setSurveys(prev => [mapGovSurveyRow(created), ...prev]);
      setForm(INITIAL_SAT);
      setShowAdd(false);
      showToast("تمت إضافة نتيجة الاستبيان");
    } catch (e) {
      showToast(e?.message || "تعذّر إضافة نتيجة الاستبيان", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.primary }}>نتائج قياس الرضا</h3>
        <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة نتيجة استبيان</Btn>
      </div>

      <div style={S.kpiRow}>
        <KPICard title="رضا المستفيدين" value={`${avgBeneficiaries}%`} icon={Smile} color={C.success} />
        <KPICard title="رضا المتبرعين" value={`${avgDonors}%`} icon={Smile} color={C.teal} />
        <KPICard title="رضا المتطوعين" value={`${avgVolunteers}%`} icon={Smile} color={C.accent} />
        <KPICard title="المتوسط العام" value={`${overallAvg}%`} icon={Award} color={C.primary} />
      </div>

      <Card style={{ padding: 20 }}>
        <h4 style={S.sectionTitle}><BarChart3 size={18} color={C.teal} /> نتائج الاستبيانات</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {surveys.map(s => {
            const barColor = s.score >= 85 ? C.success : s.score >= 70 ? C.teal : s.score >= 50 ? C.warning : C.danger;
            return (
              <div key={s.id}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div>
                    <span style={{ fontSize: 14, fontWeight: 600, color: C.primary }}>{s.title}</span>
                    <span style={{ fontSize: 11, color: C.textLight, marginRight: 8 }}>({s.audience} — {s.responses} استجابة)</span>
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: barColor }}>{s.score}%</span>
                </div>
                <div style={{ height: 10, background: C.bg, borderRadius: 5, overflow: "hidden" }}>
                  <div style={{ height: "100%", borderRadius: 5, width: `${s.score}%`, background: barColor, transition: "width 0.4s" }} />
                </div>
                <p style={{ margin: "4px 0 0", fontSize: 11, color: C.textLight }}>{s.date} — {s.note}</p>
              </div>
            );
          })}
        </div>
      </Card>

      {showAdd && (
        <Modal title="إضافة نتيجة استبيان" onClose={() => { setShowAdd(false); setForm(INITIAL_SAT); }} width={580}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="عنوان الاستبيان" required placeholder="مثال: رضا المستفيدين عن برامج رمضان" value={form.title} onChange={v => setForm({ ...form, title: v })} />
            <Field label="نوع الاستبيان (الجمهور)" type="select" options={SAT_AUDIENCES} value={form.audience} onChange={v => setForm({ ...form, audience: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="من تاريخ" type="date" value={form.periodFrom} onChange={v => setForm({ ...form, periodFrom: v })} />
              <Field label="إلى تاريخ" type="date" value={form.periodTo} onChange={v => setForm({ ...form, periodTo: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="النسبة (%)" type="number" required placeholder="مثال: 85" value={form.score} onChange={v => setForm({ ...form, score: v })} />
              <Field label="عدد المشاركين" type="number" placeholder="0" value={form.responses} onChange={v => setForm({ ...form, responses: v })} />
            </div>
            <Field label="أبرز الملاحظات" type="textarea" rows={2} placeholder="ملاحظات مهمة من الاستبيان..." value={form.note} onChange={v => setForm({ ...form, note: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Smile} onClick={handleSave}>حفظ النتيجة</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_SAT); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═════ Demo Data ═════ */
const COMPLIANCE_AREAS = [];
const STRATEGIC_DATA = { policiesActive: 0, financialsApproved: 0, activeLicenses: 0 };

const STRATEGIC_GOALS = [];
const BALANCE_SHEET = [];
const INCOME_STATEMENT = [];
const CASH_FLOW = [];
const SATISFACTION_SURVEYS = [];
const OVERDUE_DECISIONS = [];
/* ═════ Styles ═════ */
const S = {
  kpiRow: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 20, marginBottom: 16 },
  sectionTitle: { display: "flex", alignItems: "center", gap: 8, fontSize: 16, fontWeight: 700, color: C.primary, margin: "0 0 14px" },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" },
  searchBox: { display: "flex", alignItems: "center", gap: 8, background: C.bg, borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`, flex: 1, maxWidth: 320 },
  searchInput: { border: "none", background: "transparent", outline: "none", fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%" },
  detailRow: { fontSize: 14, color: C.primary, lineHeight: 1.6 },

  policiesGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 },
  policyCard: { padding: 18, cursor: "pointer" },
  policyIcon: { width: 44, height: 44, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" },

  complianceRow: { display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: `1px solid ${C.bg}` },
  complianceIcon: { width: 36, height: 36, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },

  areaRow: { display: "flex", alignItems: "center", gap: 20 },
  areaBarBg: { height: 10, borderRadius: 5, background: C.bg, overflow: "hidden" },
};
